import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from './firebase';
import { ForumRoom, ForumTopic, ForumPost } from '../types';
import { generateSearchKeywords, generateQueryKeywords } from '@2bforest/common/utils';

// ============================================================================
// FORUM ROOMS
// ============================================================================

interface ListRoomsParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot | null;
  includeInactive?: boolean;
}

interface ListRoomsResult {
  rooms: ForumRoom[];
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

export async function listRooms(params: ListRoomsParams = {}): Promise<ListRoomsResult> {
  const { pageSize = 50, lastDoc = null, includeInactive = true } = params;

  let q = query(collection(getDb(), 'forum_rooms'), orderBy('order', 'asc'));

  // Filter by active status at database level
  if (!includeInactive) {
    q = query(q, where('isActive', '==', true));
  }

  // Pagination
  q = query(q, limit(pageSize + 1));
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snapshot = await getDocs(q);
  const hasMore = snapshot.docs.length > pageSize;
  const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;

  const rooms: ForumRoom[] = docs.map(
    doc =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as ForumRoom
  );

  return {
    rooms,
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore,
  };
}

export async function createRoom(
  data: Omit<ForumRoom, 'id' | 'topicCount' | 'postCount' | 'createdAt'>
): Promise<string> {
  const roomData = {
    ...data,
    topicCount: 0,
    postCount: 0,
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(getDb(), 'forum_rooms'), roomData);
  return docRef.id;
}

export async function updateRoom(
  id: string,
  data: Partial<Omit<ForumRoom, 'id' | 'topicCount' | 'postCount' | 'createdAt'>>
): Promise<void> {
  const docRef = doc(getDb(), 'forum_rooms', id);
  await updateDoc(docRef, data);
}

export async function deleteRoom(id: string): Promise<void> {
  const docRef = doc(getDb(), 'forum_rooms', id);
  await deleteDoc(docRef);
}

export async function toggleRoomActive(id: string, isActive: boolean): Promise<void> {
  const docRef = doc(getDb(), 'forum_rooms', id);
  await updateDoc(docRef, { isActive });
}

// ============================================================================
// FORUM TOPICS
// ============================================================================

interface ListTopicsParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot | null;
  roomFilter?: string;
  searchQuery?: string;
  pinnedOnly?: boolean;
}

interface ListTopicsResult {
  topics: ForumTopic[];
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

export async function listTopics(params: ListTopicsParams = {}): Promise<ListTopicsResult> {
  const { pageSize = 50, lastDoc = null, roomFilter, searchQuery, pinnedOnly } = params;

  let q = query(collection(getDb(), 'forum_topics'), orderBy('lastActivityAt', 'desc'));

  // Filter by room at database level
  if (roomFilter) {
    q = query(q, where('roomId', '==', roomFilter));
  }

  // Filter by pinned status
  if (pinnedOnly) {
    q = query(q, where('isPinned', '==', true));
  }

  // Search by keywords at database level
  if (searchQuery && searchQuery.trim()) {
    const searchKeywords = generateQueryKeywords(searchQuery);

    if (searchKeywords.length > 0) {
      q = query(q, where('searchKeywords', 'array-contains-any', searchKeywords));
    }
  }

  // Pagination
  q = query(q, limit(pageSize + 1));
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snapshot = await getDocs(q);
  const hasMore = snapshot.docs.length > pageSize;
  const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;

  const topics: ForumTopic[] = docs.map(
    doc =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as ForumTopic
  );

  return {
    topics,
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore,
  };
}

export async function toggleTopicPinned(id: string, isPinned: boolean): Promise<void> {
  const docRef = doc(getDb(), 'forum_topics', id);
  await updateDoc(docRef, { isPinned });
}

export async function toggleTopicLocked(id: string, isLocked: boolean): Promise<void> {
  const docRef = doc(getDb(), 'forum_topics', id);
  await updateDoc(docRef, { isLocked });
}

export async function deleteTopic(id: string): Promise<void> {
  const docRef = doc(getDb(), 'forum_topics', id);
  await deleteDoc(docRef);
}

// ============================================================================
// FORUM POSTS
// ============================================================================

interface ListPostsParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot | null;
  roomFilter?: string;
  topicFilter?: string;
  searchQuery?: string;
}

interface ListPostsResult {
  posts: ForumPost[];
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

export async function listPosts(params: ListPostsParams = {}): Promise<ListPostsResult> {
  const { pageSize = 50, lastDoc = null, roomFilter, topicFilter, searchQuery } = params;

  let q = query(collection(getDb(), 'forum_posts'), orderBy('createdAt', 'desc'));

  // Filter by room at database level
  if (roomFilter) {
    q = query(q, where('roomId', '==', roomFilter));
  }

  // Filter by topic at database level
  if (topicFilter) {
    q = query(q, where('topicId', '==', topicFilter));
  }

  // Search by keywords at database level
  if (searchQuery && searchQuery.trim()) {
    const searchKeywords = generateQueryKeywords(searchQuery);

    if (searchKeywords.length > 0) {
      q = query(q, where('searchKeywords', 'array-contains-any', searchKeywords));
    }
  }

  // Pagination
  q = query(q, limit(pageSize + 1));
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snapshot = await getDocs(q);
  const hasMore = snapshot.docs.length > pageSize;
  const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;

  const posts: ForumPost[] = docs.map(
    doc =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as ForumPost
  );

  return {
    posts,
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore,
  };
}

export async function deletePost(id: string): Promise<void> {
  const docRef = doc(getDb(), 'forum_posts', id);
  await deleteDoc(docRef);
}

// ============================================================================
// FORUM TOPICS — create / update
// ============================================================================

interface CreateTopicParams {
  roomId: string;
  roomName: string;
  title: string;
  content: string;
  authorId: string;
  authorName: string;
}

/**
 * Atomically creates a new forum topic and its linked first post.
 * Firebase triggers (onForumTopicCreated / onForumPostCreated) handle
 * room counter increments — no manual counter writes needed here.
 */
export async function createTopic(params: CreateTopicParams): Promise<string> {
  const { roomId, roomName, title, content, authorId, authorName } = params;

  const topicRef = doc(collection(getDb(), 'forum_topics'));
  const postRef = doc(collection(getDb(), 'forum_posts'));

  const batch = writeBatch(getDb());

  batch.set(topicRef, {
    roomId,
    roomName,
    title,
    firstPostId: postRef.id,
    authorId,
    authorName,
    authorIsAdmin: true,
    createdAt: serverTimestamp(),
    lastActivityAt: serverTimestamp(),
    postCount: 0, // onForumPostCreated trigger will increment to 1 for the first post
    viewCount: 0,
    likeCount: 0,
    dislikeCount: 0,
    shareCount: 0,
    isPinned: false,
    isLocked: false,
    searchKeywords: generateSearchKeywords(title),
  });

  batch.set(postRef, {
    topicId: topicRef.id,
    roomId,
    content,
    authorId,
    authorName,
    authorIsAdmin: true,
    parentPostId: null,
    createdAt: serverTimestamp(),
    likeCount: 0,
    dislikeCount: 0,
    shareCount: 0,
    searchKeywords: generateSearchKeywords(content),
  });

  await batch.commit();
  return topicRef.id;
}

interface UpdateTopicParams {
  title?: string;
  isPinned?: boolean;
  isLocked?: boolean;
}

export async function updateTopic(id: string, data: UpdateTopicParams): Promise<void> {
  const payload: Record<string, unknown> = { ...data };
  if (data.title !== undefined) {
    payload.searchKeywords = generateSearchKeywords(data.title);
  }
  await updateDoc(doc(getDb(), 'forum_topics', id), payload);
}

// ============================================================================
// FORUM POSTS — create / update
// ============================================================================

interface CreatePostParams {
  topicId: string;
  roomId: string;
  content: string;
  parentPostId: string;
  authorId: string;
  authorName: string;
}

/**
 * Creates a reply post under an existing topic.
 * parentPostId is always a non-null string — admin posts are always replies.
 * Firebase triggers handle topic.postCount and room.postCount increments.
 */
export async function createPost(params: CreatePostParams): Promise<string> {
  const { topicId, roomId, content, parentPostId, authorId, authorName } = params;

  const docRef = await addDoc(collection(getDb(), 'forum_posts'), {
    topicId,
    roomId,
    content,
    authorId,
    authorName,
    authorIsAdmin: true,
    parentPostId,
    createdAt: serverTimestamp(),
    likeCount: 0,
    dislikeCount: 0,
    shareCount: 0,
    searchKeywords: generateSearchKeywords(content),
  });
  return docRef.id;
}

export async function updatePost(id: string, content: string): Promise<void> {
  await updateDoc(doc(getDb(), 'forum_posts', id), {
    content,
    searchKeywords: generateSearchKeywords(content),
    isEdited: true,
    updatedAt: serverTimestamp(),
  });
}

export async function countTopics(): Promise<number> {
  const snap = await getCountFromServer(collection(getDb(), 'forum_topics'));
  return snap.data().count;
}

export async function countRooms(): Promise<number> {
  const snap = await getCountFromServer(collection(getDb(), 'forum_rooms'));
  return snap.data().count;
}
